import { apiFetch } from "@/lib/api";
import type { AjusteDeReferencia, ClaseDeEvidencia } from "@/lib/referencias";

/**
 * Las tablas con que se homologa un comparable contra el predio tasado.
 *
 * Homologar es corregir el valor de un comparable por cada diferencia con el
 * predio —de dónde viene, cuánto mide, dónde está, qué potencial tiene, qué
 * forma— hasta dejarlo comparable. El tasador elige una categoría por ajuste en
 * cada ficha del informe, y cada categoría lleva el porcentaje que corrige.
 */

/**
 * Una opción del selector, con lo que corrige.
 *
 * El porcentaje es un NÚMERO con signo y sin símbolo: el "%" lo pone la
 * pantalla. Viajando como texto habría que parsearlo para sumar o para decidir
 * el signo, y "0" contra "0%" serían valores distintos para lo mismo.
 */
export interface CategoriaDeHomologacion {
  id: number;
  categoria: string;
  porcentaje: number;
  /**
   * A qué clase de evidencia corresponde, o `null` si la juzga una persona.
   *
   * Solo la traen las dos del ajuste por FUENTE. Con ella la ficha deduce cuál
   * le toca a la pestaña en la que está en vez de preguntárselo al tasador — y
   * la empareja por acá y NUNCA por el nombre, que es la identidad con la que
   * el seeder reconoce la fila y podría escribirse distinto mañana.
   */
  clase_evidencia: ClaseDeEvidencia | null;
}

export interface AjusteDeHomologacion {
  id: number;
  /**
   * Identidad estable, a diferencia del nombre, que es editable.
   *
   * Emparejar por nombre haría que renombrar un ajuste rompiera a quien lo
   * busque — la misma razón por la que viajan `documento_tipos.codigo` y
   * `perfil_codigo`.
   */
  codigo: string;
  nombre: string;
  descripcion: string;
  categorias: CategoriaDeHomologacion[];
}

/**
 * El ajuste que sale solo de la ficha.
 *
 * Sus categorías son CBR y Oferta, o sea la clase de evidencia, que el sistema
 * ya sabe de cada referencia. Los otros cuatro los juzga una persona mirando el
 * comparable.
 */
export const AJUSTE_POR_FUENTE = "fuente";

export async function fetchHomologacion(): Promise<AjusteDeHomologacion[]> {
  const respuesta = await apiFetch<{ data: AjusteDeHomologacion[] }>("/api/homologacion");
  return respuesta.data;
}

export interface AjusteEditado {
  descripcion: string;
  categorias: { id: number; porcentaje: number }[];
}

/**
 * Guarda un ajuste ENTERO: su descripción y todos sus porcentajes.
 *
 * No hay guardado por categoría suelta, y es a propósito: la escala se lee
 * junta —lo que significa "Mejor" depende de cuánto valgan "Levemente Mejor" y
 * "Mucho Mejor"— así que guardarla a pedazos deja peldaños nuevos al lado de
 * viejos sin que nadie pueda saber cuál es cuál mirándola.
 */
export async function guardarAjusteDeHomologacion(
  id: number,
  cambios: AjusteEditado,
): Promise<AjusteDeHomologacion> {
  const respuesta = await apiFetch<{ data: AjusteDeHomologacion }>(
    `/api/homologacion/${id}`,
    { method: "PUT", body: JSON.stringify(cambios) },
  );

  return respuesta.data;
}

/**
 * Cuánto corrige una categoría, para leerlo al lado de su nombre.
 *
 * EL SIGNO SE IMPRIME SIEMPRE, incluido el `+`, y no es prolijidad: en estas
 * escalas el signo ES la información. "Tamaño" corre al revés que los otros
 * tres —un predio más chico se paga más por hectárea, así que "Menor" SUMA— y
 * un "15 %" a secas no dice de qué lado cae. El cero va sin signo porque no
 * corrige nada.
 *
 * Los decimales no se fuerzan: la escala sembrada es entera, pero la columna es
 * `decimal` y quien la corrija desde el mantenedor puede poner un 12,5.
 */
export function porcentajeDeAjuste(porcentaje: number): string {
  const signo = porcentaje > 0 ? "+" : "";
  const cifra = new Intl.NumberFormat("es-CL", { maximumFractionDigits: 2 }).format(porcentaje);

  return `${signo}${cifra} %`;
}

/**
 * La categoría del ajuste por FUENTE que le toca a una clase de evidencia.
 *
 * POR `clase_evidencia` Y NUNCA POR EL NOMBRE. El nombre es la identidad con la
 * que el seeder reconoce la fila, así que un día que se escriba "Transacción
 * CBR" el emparejamiento por texto se rompería en silencio — y lo que se rompe
 * es qué porcentaje entra al valor de un informe pericial.
 */
export function fuenteDeLaClase(
  escala: AjusteDeHomologacion[],
  clase: ClaseDeEvidencia,
): CategoriaDeHomologacion | null {
  const fuente = escala.find((ajuste) => ajuste.codigo === AJUSTE_POR_FUENTE);
  if (fuente === undefined) return null;

  return fuente.categorias.find((c) => c.clase_evidencia === clase) ?? null;
}

/**
 * Los ajustes que de verdad corrigen a esta ficha.
 *
 * SON LOS GUARDADOS MÁS LA FUENTE DERIVADA CUANDO TODAVÍA NO ESTÁ, y sin eso la
 * pantalla se contradice sola: muestra "Fuente: Oferta −10 %" —deducido de la
 * pestaña— mientras la corrección total diría 0 %, porque el servidor recién
 * escribe esa fila cuando el tasador elige su primer ajuste. Lo que se agrega
 * acá es exactamente lo que se va a persistir en ese momento.
 *
 * Se busca por el ID DEL AJUSTE y no por el código: lo guardado no trae el
 * código, solo `ajuste_id`.
 */
export function ajustesEfectivos(
  elegidos: AjusteDeReferencia[],
  escala: AjusteDeHomologacion[],
  clase: ClaseDeEvidencia,
): AjusteDeReferencia[] {
  const fuente = escala.find((ajuste) => ajuste.codigo === AJUSTE_POR_FUENTE);
  if (fuente === undefined) return elegidos;

  if (elegidos.some((puesto) => puesto.ajuste_id === fuente.id)) return elegidos;

  const categoria = fuenteDeLaClase(escala, clase);
  if (categoria === null) return elegidos;

  return [
    ...elegidos,
    {
      ajuste_id: fuente.id,
      categoria_id: categoria.id,
      categoria: categoria.categoria,
      porcentaje: categoria.porcentaje,
    },
  ];
}

/**
 * Cuánto corrige, EN TOTAL, la homologación de una ficha.
 *
 * LOS PORCENTAJES SE SUMAN Y EL TOTAL SE APLICA UNA SOLA VEZ. No es una cascada
 * de factores multiplicados: el método lo fijó el stakeholder (10-09-2026)
 * después de ver las dos cuentas sobre el mismo comparable. La consecuencia
 * aceptada es que con la escala sembrada cinco ajustes al máximo a la baja
 * suman −90 %, y ahí el comparable queda en el 10 % de su precio.
 *
 * Suma en CENTÉSIMAS, misma regla que `sumarSuperficies`: en punto flotante
 * 0,1 + 0,2 no da 0,3, y esta cifra se muestra al lado de los porcentajes que
 * el tasador eligió — un desvío de representación aparece como una suma que no
 * cuadra con la que él hizo de cabeza.
 *
 * SALE DE LOS PORCENTAJES CONGELADOS de la ficha y nunca del catálogo vivo: es
 * la corrección que ESE informe aplica.
 */
export function correccionTotal(ajustes: AjusteDeReferencia[]): number {
  const centesimas = ajustes.reduce(
    (total, ajuste) => total + Math.round(ajuste.porcentaje * 100),
    0,
  );

  return centesimas / 100;
}

/**
 * El valor de un comparable ya corregido por su homologación.
 *
 * `null` cuando el valor base no se conoce —`uf_ha` lo es cuando no se pudo
 * calcular— porque un `0` afirmaría que el comparable vale cero.
 *
 * NO RECORTA un resultado negativo. Con la escala sembrada el piso es −90 %,
 * pero los porcentajes se editan desde el mantenedor: un `Math.max(0, …)` haría
 * que una escala mal cargada se viera razonable, y el número absurdo es
 * justamente la señal de que hay que revisarla.
 */
export function valorHomologado(
  base: number | null | undefined,
  ajustes: AjusteDeReferencia[],
): number | null {
  if (base === null || base === undefined) return null;

  return base * (1 + correccionTotal(ajustes) / 100);
}

/** El z de la normal estándar al 95 % que usa CONFIDENCE.NORM de Excel. */
const Z_95 = 1.959964;

/**
 * La estadística de la muestra que los informes reales imprimen bajo "Análisis
 * estadístico" (verificada al peso contra el informe 091).
 *
 * ES EL GEMELO de `EstadisticaDeLaMuestra` en el api (`CalculadorDelValorDeReferencia`
 * la persiste): cambiar una fórmula acá obliga a cambiarla allá EN EL MISMO COMMIT.
 *
 * - Desviación MUESTRAL (n − 1).
 * - IC 95 % = 1,959964 · DE / √n.
 * - Límites comerciales = media ∓ IC, SIN recortar: el inferior puede ser negativo.
 * - Con menos de dos valores no hay desviación, intervalo ni límites: `null`,
 *   nunca cero.
 */
export interface EstadisticaDeLaMuestra {
  n: number;
  media: number;
  desviacion: number | null;
  ic95: number | null;
  limiteInferior: number | null;
  limiteSuperior: number | null;
  minimo: number;
  maximo: number;
}

export function estadisticaDeLaMuestra(valores: number[]): EstadisticaDeLaMuestra | null {
  const n = valores.length;
  if (n === 0) return null;

  const media = valores.reduce((suma, v) => suma + v, 0) / n;
  const minimo = Math.min(...valores);
  const maximo = Math.max(...valores);

  if (n < 2) {
    return {
      n,
      media,
      desviacion: null,
      ic95: null,
      limiteInferior: null,
      limiteSuperior: null,
      minimo,
      maximo,
    };
  }

  const cuadrados = valores.reduce((suma, v) => suma + (v - media) ** 2, 0);
  const desviacion = Math.sqrt(cuadrados / (n - 1));
  const ic95 = (Z_95 * desviacion) / Math.sqrt(n);

  return {
    n,
    media,
    desviacion,
    ic95,
    limiteInferior: media - ic95,
    limiteSuperior: media + ic95,
    minimo,
    maximo,
  };
}
